import { Component } from "react";
import axios from "axios";
import myAxios from "./axiosilearnWrapper";
import Config from "../_config/config";
import UserService from "./UserService";

// Mirror axiosilearnWrapper's suppression on the plain (default) axios instance.
// ApiService/ConsentService call raw `axios` (not myAxios), so without this a
// 401/403 rejects and lands in CRA's "Uncaught runtime errors" overlay. The team
// intent (see axiosilearnWrapper) is that ilearn swallows all 401/403 as
// session/auth issues, not app bugs — components handle missing data via their
// own state.
axios.interceptors.response.use(
  response => response,
  error => {
    const url = error.config?.url || '';
    const status = error.response?.status;

    const isFlipClassRoute = url.includes('flip-class') ||
                             url.includes('lesson-plan') ||
                             url.includes('studyresource') ||
                             url.includes('study/resource');
    // Flip-class/lesson-plan/study-resource callers expect collection-shaped
    // data; everyone else gets an empty object like the wrapper returns.
    const suppressedBody = isFlipClassRoute
      ? { attachments: [], groups: [], lessons: [], data: [] }
      : {};

    // Suppress 401/403 — but NOT blob downloads: a fake {} body would become a
    // garbage blob and hide the failure. Let those reject so the caller sees it.
    if ((status === 401 || status === 403) && error.config?.responseType !== 'blob') {
      console.warn('[axios] Auth error suppressed (plain axios):', status, url);

      // 401 (expired/invalid token) surfaces the session-expired modal, but only
      // when a token was actually present (a real session expired mid-use) and
      // the failing call isn't the login itself (wrong-password 401s excluded).
      const isLoginUrl = url.includes('/authenticate/user') || url.includes('/login');
      const hadSession = !!UserService.getToken();
      if (status === 401 && !isLoginUrl && hadSession) {
        window.dispatchEvent(new CustomEvent('ilearn:session-expired'));
      }

      return Promise.resolve({
        data: suppressedBody,
        status: 200,
        statusText: 'OK (suppressed auth error)',
        headers: {},
        config: error.config
      });
    }

    // Keep the existing flip-class 500 suppression.
    if (status === 500) {
      console.error('Server error (plain axios):', url, error.message);
      if (isFlipClassRoute) {
        console.log('Suppressing 500 for flip class route (plain axios):', url);
        return Promise.resolve({
          data: suppressedBody,
          status: 200,
          statusText: 'OK (suppressed server error)',
          headers: {},
          config: error.config
        });
      }
    }

    return Promise.reject(error);
  }
);

class ApiService extends Component {
  fetchHome() {
    // initApiUrl switches java/node on authBackend (see config.js). Node port:
    // nodefork/src/controllers/ilearn-init/get-ilearn-institute-home.controller.ts
    return axios.get(Config.initApiUrl + "init/institute/home");
  }

  getActiveTheme() {
    // initApiUrl switches java/node on authBackend (see config.js). Node port:
    // nodefork/src/controllers/ilearn-init/get-ilearn-theme.controller.ts
    return axios.get(Config.initApiUrl + "init/theme");
  }

  getData(url) {
    return axios.get(url, {
      "access-control-allow-origin": "*",
    });
  }
  getDeviceId() {
    let deviceId = localStorage.getItem("ilearn-pwa-deviceId");
    if (!deviceId) {
      console.log("DeviceId Not found generating new");
      let sixdigitsrandom = Math.floor(100000 + Math.random() * 900000);
      deviceId = "PWA-" + sixdigitsrandom;
      localStorage.setItem("ilearn-pwa-deviceId", deviceId);
    }
    return deviceId;
  }

  generateEndPoint(postJSON) {
    let url = Config.apiUrl + "student/generate/push/endpoint";
    console.log(url);
    return axios.post(url, postJSON);
  }

  getTaskResources(postJSON) {
    var enquiryId = localStorage.getItem("enquiryId");
    let url = Config.apiUrl + "study/resource/enquiry/" + enquiryId;
    console.log(url);
    return axios.post(url, postJSON);
  }

  updateTaskResource(postJSON) {
    let url = Config.apiUrl + "study/resource/save/markdone";
    console.log(url);
    return axios.post(url, postJSON);
  }

  fetchLogin(username, password, metaData = null) {
    var dataString = {
      userName: username,
      password: password,
      deviceId: this.getDeviceId(),
    };
    if (metaData && Object.keys(metaData).length > 0) {
      dataString.metaData = metaData;
    }
    const url = Config.authBaseUrl + "rest/mobile/authenticate/user";
    console.log("Authentication URL:", url);
    console.log("Auth Backend:", Config.authBackend);
    return axios.post(url, dataString, {
      "access-control-allow-origin": "*",
    });
  }

  fetchLoginToken(token) {
    var dataString = {
      token: token,
      deviceId: this.getDeviceId(),
    };
    // Always use Java API directly for token login (Node doesn't support token login)
    const url = Config.siteUrl + "rest/mobile/authenticate/user";
    console.log("Token Authentication URL:", url);
    return axios.post(url, dataString, {
      "access-control-allow-origin": "*",
    });
  }

  verifyOTP(userId, otp) {
    var dataString = {
      userId: userId,
      otp: otp,
      deviceId: this.getDeviceId(),
    };
    return axios.post(Config.baseUrl + "verify/otp", dataString);
  }

  openIdLogin(credentials) {
    let url = Config.siteUrl + "rest/mobile/authenticate/user/openid";
    console.log(url);
    return axios.post(url, credentials);
  }

  fetchRegisterList() {
    return axios.get(Config.siteUrl + "api/common/quickEnquiry/form/3");
  }

  fetchRegister(data) {
    var dataString = data;
    //console.log("Register JSON : "+JSON.stringify(dataString));
    console.log(Config.siteUrl + "api/common/quickEnquiry/save/3");
    console.log(data);
    return axios.post(
      Config.siteUrl + "api/common/quickEnquiry/save/3",
      dataString
    );
  }

  fetchForgetpwd(username) {
    var dataString = {
      userName: username,
    };
    return axios.post(Config.nodeSiteUrl + "v1/password/recover", dataString);
  }

  fetchLoginUserName(registeredEmail) {
    var dataString = {
      registeredEmail: registeredEmail,
    };
    // Node endpoint — keeps "Forgot username?" working on node-only tenants
    // where Tomcat is shut off.
    return axios.post(Config.nodeSiteUrl + "v1/username/recover", dataString);
  }

  fetchResetpwd(username, otp, newPassword, confirmPassword) {
    var dataString = {
      userName: username,
      otp: otp,
      newPassword: newPassword,
      confirmPassword: confirmPassword,
    };
    return axios.post(Config.nodeSiteUrl + "v1/password/reset", dataString);
  }

  fetchChangePwd(username, currentPassword, newPassword, confirmPassword) {
    var dataString = {
      userName: username,
      currentPassword: currentPassword,
      newPassword: newPassword,
      confirmPassword: confirmPassword,
    };
    return axios.post(Config.apiUrl + "password/edit", dataString);
  }

  fetchDashboard() {
    var enquiryId = localStorage.getItem("enquiryId");
    // 30s timeout: a backend hung on a dead DB connection must surface as an
    // error (visible banner) rather than leave the dashboard pending forever.
    return myAxios.get(
      Config.nodeSiteUrl + "v1/dashboard/init/enquiry/" + enquiryId,
      { timeout: 30000 }
    );
  }

  fetchTestList(url) {
    var enquiryId = localStorage.getItem("enquiryId");
    const fetchUrl =
      url || Config.nodeSiteUrl + "v1/student-tests/student/" + enquiryId + "/tests/category/0";
    return myAxios.get(fetchUrl);
  }

  // Tenant runtime config from customer_detail_master.iLearnAppConfig
  getIlearnAppConfig() {
    return myAxios.get(Config.nodeSiteUrl + "v1/common/ilearn-app-config");
  }

  // SCORM: per-learner launch — creates this learner's SCORM Cloud
  // registration on first call and returns a fresh short-lived launch URL.
  launchScormCourse(studyResourceId) {
    return myAxios.post(
      Config.nodeSiteUrl + "v1/scorm/courses/" + studyResourceId + "/launch",
      {}
    );
  }

  // Per-tenant backend switch for the online-test player calls
  // (customer_detail_master.iLearnAppConfig.onlineTest.backend). Cached in
  // sessionStorage at app bootstrap (see App.jsx cacheIlearnAppConfig). Defaults
  // to "java" so a tenant keeps the existing Java behaviour unless opted in.
  getOnlineTestBackend() {
    try {
      const cfg = JSON.parse(sessionStorage.getItem("iLearnAppConfig") || "{}");
      return cfg?.onlineTest?.backend === "node" ? "node" : "java";
    } catch (e) {
      return "java";
    }
  }

  // Tenant feature flags (customer_detail_master.iLearnAppConfig.features).
  // Default-on: a feature is enabled unless its key is explicitly false.
  // Admin-editable from iteach Tenant Preferences → iLearn Features. Reads the
  // sessionStorage cache seeded at bootstrap (App.jsx cacheIlearnAppConfig).
  isFeatureEnabled(key) {
    try {
      const cfg = JSON.parse(sessionStorage.getItem("iLearnAppConfig") || "{}");
      return cfg?.features?.[key] !== false;
    } catch (e) {
      return true;
    }
  }

  fetchTest(testid) {
    var enquiryId = localStorage.getItem("enquiryId");
    var dataString = {
      deviceId: this.getDeviceId(),
      enquiryId: Number(enquiryId) || 0,
      testId: Number(testid) || 0,
    };
    return myAxios.post(
      Config.nodeSiteUrl + "v1/student-tests/test/download/init",
      dataString,
    );
  }

  testisActive(testid) {
    return myAxios.get(Config.nodeSiteUrl + "v1/student-tests/" + testid + "/is-active");
  }

  fetchTestJson(url) {
    // var enquiryId = localStorage.getItem('enquiryId');
    return axios.get(url);
  }

  endExam(json_data) {
    return axios.post(Config.apiUrl + "test/submit", json_data);
  }

  fetchTestPaperUploadStatus(testid) {
    var enquiryId = localStorage.getItem("enquiryId");
    return axios.get(
      Config.siteUrl +
        "api/common/student/" +
        enquiryId +
        "/test/" +
        testid +
        "/view/dvs/paper"
    );
  }

  fetchTestResult(body) {
    const url = `${Config.lambda.testAnalysis}/?mode=analysis`;
    console.log(url);
    return fetch(url, {
      method: "post",
      body: JSON.stringify(body),
    }).then((res) => res.json());
  }

  // Unified test-result / analysis fetch. On node-backend tenants the whole
  // result payload (result-viewer mode, testConfig, testTableData, barGraphData,
  // question-wise sections) comes from the nodefork test-result endpoint, which
  // builds it in the same shape the legacy testAnalysis Lambda returned (the
  // Lambda proxied to the Java monolith). Other tenants keep the Lambda.
  // Normalized to { result, data } so callers are backend-agnostic.
  async fetchResultUnified(testId, enquiryId, body) {
    if (Config.onlineTestFns === "node") {
      const res = await this.fetchDirectTestResult(testId, enquiryId);
      const b = (res && res.data) || {};
      return { result: b.result, data: b.data };
    }
    return this.fetchTestResult(body);
  }

  fetchDirectTestResult(testId, enquiryId) {
    return myAxios.get(
      Config.nodeSiteUrl + "v1/test-result/" + testId + "/student/" + enquiryId
    );
  }

  fetchDirectTestResultForAttempt(testId, enquiryId, attemptNumber) {
    return myAxios.get(
      Config.nodeSiteUrl + "v1/test-result/" + testId + "/student/" + enquiryId + "?attemptNumber=" + attemptNumber
    );
  }

  getTestAttemptInfo(testId, enquiryId) {
    return myAxios.get(
      Config.nodeSiteUrl + "v1/test-result/" + testId + "/student/" + enquiryId + "/attempts"
    );
  }

  initiateRetake(testId, enquiryId, enrollmentId) {
    return myAxios.post(
      Config.nodeSiteUrl + "v1/test-result/" + testId + "/student/" + enquiryId + "/retake",
      { enrollmentId }
    );
  }

  getTestCategoryWiseTestList(testCategoryId) {
    var enquiryId = localStorage.getItem("enquiryId");
    return myAxios.get(
      Config.nodeSiteUrl +
        "v1/student-tests/student/" +
        enquiryId +
        "/tests/category/" +
        testCategoryId
    );
  }

  // Quiz Zone player + analysis moved to nodefork — see GamificationService
  // (v1/gamification/quiz-zone/:id/play|submit|result). The legacy Java
  // rest/mobile getOnlineTestData / student/test/submit / sync/time /
  // server/status / analysis endpoints are no longer called from ilearn.

  // Wallet + leaderboard moved to nodefork — see GamificationService
  // (v1/gamification/wallet, v1/gamification/leaderboard). The legacy Java
  // api/mobile wallet/leaderboard endpoints are no longer called from ilearn.

  // Learning modules moved to nodefork — the legacy Java rest/mobile
  // study-resource + learningmodules endpoints are no longer called.
  fetchLearning(rid, rsid) {
    var enquiryId = localStorage.getItem("enquiryId");
    const url = Config.nodeSiteUrl + "v1/study-resources/student/golden";
    const payload = {
      enquiryId: enquiryId,
      resourceTypeId: Number(rid),
      resourceSubTypeIds: String(rsid ?? "0"),
    };
    // Golden API wraps the list in { data, status }; callers expect res.data
    // to be the bare array (old Java contract), so unwrap here.
    return myAxios
      .post(url, payload)
      .then((res) => ({ ...res, data: res.data?.data ?? [] }));
  }

  fetchLearningMetaData(moduleIdsJson) {
    var url = Config.nodeSiteUrl + "v1/learning-modules/meta-data";
    return myAxios.post(url, moduleIdsJson);
  }

  fetchStudentLearningActivities(payload) {
    var url = Config.nodeSiteUrl + "v1/learning-modules/student-activity";
    return myAxios.post(url, payload);
  }

  fetchLearningSteps(learnId) {
    return axios.get(Config.apiUrl + "lessonplan/" + learnId + "/steps");
  }

  fetchLearningStepsDesktop(learnId) {
    // Migrated off the Java desktop API. Node returns the same {name, groups: [{steps: [{resources: [{completed}]}]}]} shape,
    // with `completed` driven by user_study_resource_submission so the sidebar progress reflects server truth.
    const batchId = localStorage.getItem("batchId");
    const enquiryId = localStorage.getItem("enquiryId");
    const params = [];
    if (batchId) params.push("batchId=" + batchId);
    if (enquiryId) params.push("enquiryId=" + enquiryId);
    const qs = params.length ? "?" + params.join("&") : "";
    return myAxios.get(
      Config.nodeSiteUrl + "v1/study-resources/lesson-plans/" + learnId + qs,
    );
  }

  getStudyResourceById(resourceId) {
    return myAxios.get(Config.nodeSiteUrl + "v1/study-resources/" + resourceId);
  }

  markStudyResourceRead(studyResourceId, lessonPlanId) {
    const promise = myAxios.post(Config.nodeSiteUrl + "v1/common/study-resource/mark-read", {
      studyResourceId,
      lessonPlanId,
    });
    // Tell the app-wide CredentialUnlockWatcher to look for a freshly
    // issued credential. The auto-issue cascade runs server-side on the
    // same /mark-read call, so a credential may have just been minted.
    promise
      .then(() => {
        if (typeof window.notifyCredentialMayHaveChanged === "function") {
          window.notifyCredentialMayHaveChanged();
        }
      })
      .catch(() => {});
    return promise;
  }

  getLessonPlanProgress(lessonPlanId) {
    return myAxios.get(Config.nodeSiteUrl + "v1/common/lesson-plan/" + lessonPlanId + "/progress");
  }

  getFlipClassResources(enrollmentId, stepId) {
    // Get flip class resources filtered by current lesson status
    return myAxios.get(
      Config.nodeSiteUrl +
      "flip-class/student/" + enrollmentId + "/step/" + stepId + "/resources"
    );
  }

  fetchFlipClassConfig() {
    // Get flip class phase configuration from database
    return myAxios.get(Config.nodeSiteUrl + "v1/flip-class/config");
  }

  fetchLessonPlanWithTags(lessonPlanId, batchId, enquiryId) {
    // Get lesson plan structure with all resources and tags from Node backend
    // Pass batchId (preferred) or enquiryId to fetch step status information
    let url = Config.nodeSiteUrl + "v1/study-resources/lesson-plans/" + lessonPlanId;

    // Build query parameters - prefer batchId if available, fall back to enquiryId
    const params = [];
    if (batchId) {
      params.push("batchId=" + batchId);
    }
    if (enquiryId) {
      params.push("enquiryId=" + enquiryId);
    }

    if (params.length > 0) {
      url += "?" + params.join("&");
    }

    return myAxios.get(url);
  }

  fetchLearningStepAttachments(stepId, lessonPlanId) {
    // Migrated to Node backend instead of Java backend
    // Node backend returns full lesson plan structure, we'll extract step resources

    // Get batchId or enquiryId for access control
    const batchId = localStorage.getItem("batchId");
    const enquiryId = localStorage.getItem("enquiryId");

    let url = Config.nodeSiteUrl + "v1/study-resources/lesson-plans/" + lessonPlanId;

    // Build query parameters
    const params = [];
    if (batchId) {
      params.push("batchId=" + batchId);
    }
    if (enquiryId) {
      params.push("enquiryId=" + enquiryId);
    }
    if (params.length > 0) {
      url += "?" + params.join("&");
    }

    console.log("Fetching lesson plan from Node backend:", url);

    return myAxios.get(url).then((response) => {
      // Transform Node backend response to match Java backend format
      const lessonPlan = response.data;

      // Find the step with the given stepId
      let stepResources = [];
      if (lessonPlan.groups) {
        for (const group of lessonPlan.groups) {
          if (group.steps) {
            for (const step of group.steps) {
              if (step.id.toString() === stepId.toString()) {
                stepResources = step.resources || [];
                break;
              }
            }
          }
          if (stepResources.length > 0) break;
        }
      }

      // Helper function to map studyResourceTypeName to the type field expected by Resource.jsx
      const mapResourceType = (resource) => {
        if (resource.resourceType === 'test') return 'test';

        const typeName = resource.studyResourceTypeName?.toLowerCase() || '';

        // Map based on studyResourceTypeName
        if (typeName.includes('pdf') || typeName.includes('book') || typeName.includes('e-book')) {
          return 'book'; // PDF viewer will handle both PDFs and books
        } else if (typeName.includes('message')) {
          return 'message';
        } else if (typeName.includes('url') || typeName.includes('link')) {
          return 'url';
        } else if (typeName.includes('image')) {
          return 'image';
        } else if (typeName.includes('video')) {
          return 'streaming';
        }

        // Default to 'resource' for everything else
        return 'resource';
      };

      // Transform resources to match the format expected by Resource.jsx component
      const transformedResources = stepResources.map(resource => ({
        ...resource,
        // Add aliases for Resource.jsx component compatibility
        type: mapResourceType(resource),
        typeName: resource.studyResourceTypeName,
        typeId: resource.studyResourceTypeId,
        subTypeId: resource.studyResourceSubTypeId,
        publicUrl: resource.url,
        // Add studyResourceType for quiz/test detection in changeContent method
        studyResourceType: resource.resourceType === 'test' ? '0' : (resource.studyResourceTypeId?.toString() || '1'),
      }));

      // Return in the format expected by LearningStepAttachmentDetails
      // Include lesson-plan-level metaData so instructor details are available
      return {
        data: {
          attachments: transformedResources,
          metaData: lessonPlan.metaData || null
        }
      };
    });
  }

  fetchResources(rid, rsid) {
    var enquiryId = localStorage.getItem("enquiryId");
    let ran = Math.floor(100000 + Math.random() * 900000);
    let url =
      Config.apiUrl +
      "study/resource/type/" +
      rid +
      "/subtype/" +
      rsid +
      "/page/0/enquiry/" +
      enquiryId +
      "?var=" +
      ran;
    console.log(url);
    return axios.get(url);
  }

  fetchAttendance() {
    var enquiryId = localStorage.getItem("enquiryId");
    return myAxios.get(Config.nodeSiteUrl + "v1/attendance/summary/student/" + enquiryId);
  }

  fetchBiometricAttendance() {
    var enquiryId = localStorage.getItem("enquiryId");
    return axios.get(
      Config.apiUrl + "biometric/attendance/student/" + enquiryId + "/source/2"
    );
  }

  fetchProduct() {
    // Single nodefork-backed catalog. Logged-in students get the
    // entitlement/enrollment-annotated variant; logged-out visitors get
    // the same shape without the per-student flags.
    // Optional ?providerSchoolId filter is passed through if present.
    const enquiryId = localStorage.getItem("enquiryId");
    const hasStudent =
      enquiryId && enquiryId !== "null" && enquiryId !== "undefined";
    const baseUrl = hasStudent
      ? `${Config.nodeSiteUrl}v1/products/catalog/student`
      : `${Config.nodeSiteUrl}v1/products/catalog`;
    const params = {};
    if (hasStudent) params.enquiryId = Number(enquiryId);
    return (hasStudent ? myAxios : axios)
      .get(baseUrl, { params })
      .then((res) => ({ ...res, data: res.data?.data ?? [] }));
  }

  // Served by nodefork — same { status, message, data } shape as the old
  // Java api/common/product/:id endpoint (cost as "0.0"-style string).
  getProduct(productId) {
    return axios.get(
      Config.nodeSiteUrl + "v1/products/" + productId + "/summary"
    );
  }

  // New CMS-backed product details (public — no auth required).
  // Returns product_master + product_cms joined; falls back to product_master
  // when no CMS row exists. Used by the new template-driven ProductDetails page.
  // Pass enquiryId (if logged-in) so backend includes hasEntitlement.
  getProductDetails(productId) {
    const enquiryId = localStorage.getItem("enquiryId");
    const params = {};
    if (enquiryId && enquiryId !== "null" && enquiryId !== "undefined") {
      params.enquiryId = Number(enquiryId);
    }
    return axios.get(
      Config.nodeSiteUrl + "v1/products/" + productId + "/details",
      { params },
    );
  }

  // Guest checkout — public. Creates a login-less student_enquiry + user for a
  // logged-out buyer and returns a login-shaped payload (with a JWT + isGuest)
  // so the existing Subscribe → pay → enroll flow can run as if signed in.
  // On a collision the response carries { alreadyRegistered: true } instead.
  guestCheckoutRegister(payload) {
    return axios.post(
      Config.nodeSiteUrl + "v1/guest-checkout/register",
      payload,
    );
  }

  // Secures the guest account with a password (authed — the buyer holds the
  // guest JWT). Called from the payment-result screen after the charge lands.
  guestSetPassword(password) {
    return myAxios.post(
      Config.nodeSiteUrl + "v1/guest-checkout/set-password",
      { password },
    );
  }

  // Brochure lead capture — public. Records {name, email, phone} and
  // returns the brochure URL the client should open. 404 if no brochure set.
  createBrochureLead(productId, lead) {
    return axios.post(
      Config.nodeSiteUrl + "v1/products/" + productId + "/brochure-lead",
      lead,
    );
  }

  // Campaign visit beacon — public. Fired by ProductDetails when the page
  // loads with a ?cw_cmp=<trackingCode> query param from a tracked ad link.
  logCampaignVisit(productId, payload) {
    return axios.post(
      Config.nodeSiteUrl + "v1/products/" + productId + "/campaign-visit",
      payload,
    );
  }

  preOrderProduct(productId) {
    let enquiryId = localStorage.getItem("enquiryId");
    let url =
      Config.siteUrl +
      "rest/mobile/ilearn/student/" +
      enquiryId +
      "/product/" +
      productId +
      "/pre/order";
    console.log(url);
    return axios.get(url);
  }

  fetchReferrals() {
    var enquiryId = localStorage.getItem("enquiryId");
    return myAxios.get(Config.nodeSiteUrl + "v1/referrals/student/" + enquiryId);
  }

  fetchRefer(data) {
    var dataString = data;
    return myAxios.post(Config.nodeSiteUrl + "v1/referrals/submit", dataString);
  }

  fetchCountries() {
    return myAxios.get(Config.nodeSiteUrl + "v1/referrals/countries");
  }

  fetchAnalysis() {
    var enquiryId = localStorage.getItem("enquiryId");
    return axios.get(Config.baseUrl + "analysis/tests/" + enquiryId);
  }

  getNotificationMessages() {
    var enquiryId = localStorage.getItem("enquiryId");
    return myAxios.get(Config.nodeSiteUrl + "v1/dashboard/student/" + enquiryId + "/notification/messages");
  }

  getPayments() {
    var enquiryId = localStorage.getItem("enquiryId");
    let url = Config.siteUrl + "rest/mobile/payments/" + enquiryId;
    console.log(url);
    return axios.get(url);
  }

  // getPaymentDetails(enrollmentId) {
  //     let url = Config.siteUrl +"rest/mobile/payments/enrollment/"+enrollmentId;
  //     console.log(url);
  //     return axios.get(url);
  // }
  getPaymentDetails(enrollmentId, receiptSeriesId) {
    const url = `${Config.nodeSiteUrl}v1/enrollment/${enrollmentId}/plan-and-payments?receiptSeriesId=${receiptSeriesId}`;
    return myAxios.get(url);
  }

  getReceiptSeries(enrollmentId) {
    const url = `${Config.siteUrl}api/common/receiptseries?enrollmentId=${enrollmentId}`;
    return axios.get(url);
  }

  getPaymentHistory(enrollmentId) {
    let url = Config.siteUrl + "rest/mobile/payments/history/" + enrollmentId;
    console.log(url);
    return axios.get(url);
  }

  getTimeTables(data) {
    return myAxios.post(Config.nodeSiteUrl + "v1/timetable/student", data);
  }

  getStudentStudyResources(data) {
    return myAxios.post(
      Config.nodeSiteUrl + "v1/study-resources/student/golden",
      data
    );
  }

  getRecordedVideos(data) {
    return myAxios.post(
      Config.nodeSiteUrl + "v1/timetable/student/recorded-videos",
      data
    );
  }

  uploadFile(formData, testId) {
    var enquiryId = localStorage.getItem("enquiryId");
    return axios.post(
      Config.siteUrl +
        "api/common/student/" +
        enquiryId +
        "/test/" +
        testId +
        "/upload/dvs/paper",
      formData
    );
  }

  fetchExams(testCategoryIds, secondsActiveAfterExamSubmit) {
    var enquiryId = localStorage.getItem("enquiryId");
    return myAxios.get(
      Config.nodeSiteUrl +
        "v1/student-tests/exams/category/" +
        testCategoryIds +
        "/enquiry/" +
        enquiryId +
        "/since/" +
        secondsActiveAfterExamSubmit
    );
  }

  getDiscussionBoards() {
    // Migrated to nodefork — JWT comes through myAxios; the backend keys off req.user.
    return myAxios.get(Config.nodeSiteUrl + "v1/discussion-board/boards");
  }

  getDiscussionBoardTopics(discussionBoardId) {
    return myAxios.get(
      Config.nodeSiteUrl + "v1/discussion-board/boards/" + discussionBoardId,
    );
  }

  getDiscussionBoardTopic(topicId) {
    return myAxios.get(
      Config.nodeSiteUrl + "v1/discussion-board/topics/" + topicId,
    );
  }

  getDiscussionBoardQuestionTopic(questionId) {
    return myAxios.get(
      Config.nodeSiteUrl + "v1/discussion-board/topics/question/" + questionId,
    );
  }

  getDiscussionBoardResourceTopic(resourceId) {
    return myAxios.get(
      Config.nodeSiteUrl + "v1/discussion-board/topics/resource/" + resourceId,
    );
  }

  getDiscussionBoardDisclaimer() {
    return myAxios.get(Config.nodeSiteUrl + "v1/discussion-board/disclaimer");
  }

  addTopic(topic) {
    return myAxios.post(Config.nodeSiteUrl + "v1/discussion-board/topics", topic);
  }

  createComment(comment) {
    return myAxios.post(Config.nodeSiteUrl + "v1/discussion-board/comments", comment);
  }

  replyComment(comment) {
    // ilearn passes { id, postId, replyContent, replyUserId } — backend ignores replyUserId
    // (always uses req.user) but the shape is otherwise unchanged.
    return myAxios.post(Config.nodeSiteUrl + "v1/discussion-board/replies", comment);
  }

  deleteCommentOrReply(data) {
    return myAxios.post(Config.nodeSiteUrl + "v1/discussion-board/delete", data);
  }

  togglePinDiscussionTopic(data) {
    return myAxios.post(Config.nodeSiteUrl + "v1/discussion-board/pin", data);
  }

  submitTest(testId, userId) {
    // Node backend (tenant-flag opt-in): derives userId/enquiryId from the JWT.
    if (this.getOnlineTestBackend() === "node") {
      return myAxios.post(
        Config.nodeSiteUrl + "v1/online-tests/" + testId + "/submit"
      );
    }
    return axios.post(
      Config.siteUrl +
        "api/tm/" +
        testId +
        "/user/" +
        userId +
        "/submitAndPopulate"
    );
  }

  fetchInstantResult(testId, userId) {
    return axios.get(
      Config.nodeSiteUrl +
        "v1/quick-quiz/instant-result/" +
        testId +
        "?userId=" +
        userId
    );
  }

  checkTestStatus(testId) {
    var enquiryId = localStorage.getItem("enquiryId");
    let url =
      Config.desktopApi + "tm/" + testId + "/enquiry/" + enquiryId + "/status";
    return axios.get(url);
  }

  resumeStudyResourcesLearningActivity(Id) {
    // nodefork replacement for legacy Java
    //   GET /api/studyresources/learningactivity/{Id}/track/last
    // Response body is the parsed LastAccessedObject
    // ({ groupIndex, stepIndex, resourceIndex, type, id }) or {} when none.
    return myAxios.get(
      Config.nodeSiteUrl + "v1/learning-modules/last-position/" + Id
    );
  }

  trackStudyResourcesLearningActivity(Id, data) {
    // nodefork replacement for legacy Java
    //   POST /api/studyresources/learningactivity/{Id}/track
    // Standalone-resource callers pass Id = null; the Node controller
    // treats that as a no-op (matches Java behaviour).
    return myAxios.post(
      Config.nodeSiteUrl + "v1/learning-modules/last-position",
      { learningModuleId: Id, position: data }
    );
  }

  saveStudyResourcesNotes(data, type) {
    // Use new nodefork endpoint
    let url =
      Config.nodeSiteUrl +
      "v1/study-resources/" +
      data.resourceId +
      "/notes/save";

    // Transform payload to match new API format
    const payload = {
      userId: data.userId,
      note: data.note,
      assetMarker: data.time // Map 'time' to 'assetMarker'
    };

    return myAxios.post(url, payload);
  }

  getCertifyingLetterData() {
    let url = Config.apiUrl + "certify/letter";
    let user = JSON.parse(localStorage.getItem("user")) || {};
    let enquiryId = localStorage.getItem("enquiryId");
    let enrollmentId = user?.enrollmentBeans?.[0]?.enrollmentId;

    let postData = {
      enquiryId: enquiryId,
      userId: user.userId || user.id,
      enrollmentId: enrollmentId
    };

    console.log(url);
    return myAxios.post(url, postData);
  }
  updateStudyResourceNote(resourceId, userId, noteIndex, data) {
    // Use new nodefork endpoint for updating a note
    let url =
      Config.nodeSiteUrl +
      "v1/study-resources/" +
      resourceId +
      "/user/" +
      userId +
      "/notes/" +
      noteIndex;

    const payload = {
      note: data.note,
      assetMarker: data.assetMarker
    };

    return myAxios.put(url, payload);
  }
  deleteStudyResourceNote(resourceId, userId, data) {
    // Use new nodefork endpoint
    let url =
      Config.nodeSiteUrl +
      "v1/study-resources/" +
      resourceId +
      "/user/" +
      userId +
      "/notes/" +
      data.noteIndex;
    return myAxios.delete(url);
  }
  loadStudyResourcesNotes(resourceId, type) {
    // Use new nodefork endpoint
    let url =
      Config.nodeSiteUrl +
      "v1/study-resources/" +
      resourceId +
      "/user/" +
      UserService.getUserId() +
      "/notes";
    return myAxios.get(url);
  }

  fetchStudyResourcesNotes(id) {
    let url =
      Config.desktopApi +
      "studyresources/lp/" +
      id +
      "/user/" +
      UserService.getUserId() +
      "/notes";
    return axios.get(url);
  }

  studentPayCheck(data) {
    let user = JSON.parse(localStorage.getItem("user")) || [];
    let url =
      Config.siteUrl + "api/common/student/access/paycheck?token=" + user.token;
    return axios.post(url, data);
  }

  studentTickerMessages() {
    var enquiryId = localStorage.getItem("enquiryId");
    let url = Config.nodeSiteUrl + "v1/study-resources/student/golden?tickerMessage=true";
    let payload = {
      enquiryId: enquiryId,
      resourceTypeId: 6,
      resourceSubTypeIds: "0"
    };
    return myAxios.post(url, payload);
  }

  initTestTaking(testId, paperUrl) {
    let data = {
      userAgent: navigator.userAgent,
      webCam: false,
      paperUrl
      // "ipAddress": ip.address(),
    };
    // Node backend (tenant-flag opt-in): derives userId from the JWT.
    if (this.getOnlineTestBackend() === "node") {
      return myAxios.post(
        Config.nodeSiteUrl + "v1/online-tests/" + testId + "/init",
        data
      );
    }
    let url =
      Config.desktopApi +
      "tm/" +
      testId +
      "/user/" +
      UserService.getUserId() +
      "/initTestTaking";
    return axios.post(url, data);
  }

  fetchTodayTests(todayTestsConfig) {
    let enquiryId = localStorage.getItem("enquiryId");
    let url =
      Config.nodeSiteUrl +
      "v1/student-tests/student/" +
      enquiryId +
      "/today";
    console.log(url);
    return myAxios.post(url, todayTestsConfig);
  }

  getFeedbackForm(formId, testId, employeeId, courseId, applicationUserId) {
    let enquiryId = localStorage.getItem("enquiryId");
    let userId = applicationUserId || 0;
    let url =
      Config.siteUrl +
      "api/studyresources/feedback/form/" +
      formId +
      "/enquiry/" +
      enquiryId +
      "/test/" +
      testId +
      "/instructor/" +
      employeeId +
      "/course/" +
      courseId +
      "?applicableToUserId=" +
      userId;
    console.log(url);
    return axios.get(url);
  }

  saveFeedbackForm(feedbackFormBean, testId, userId) {
    let enquiryId = localStorage.getItem("enquiryId");
    let url =
      Config.siteUrl +
      "api/studyresources/feedback/form/save/enquiry/" +
      enquiryId +
      "/user/" +
      userId +
      "/test/" +
      testId;
    console.log(url);
    return axios.post(url, feedbackFormBean);
  }

  getInstructorFeedbackForms() {
    let enquiryId = localStorage.getItem("enquiryId");
    let url = Config.apiUrl + "instructor/feedback/enquiry/" + enquiryId;
    console.log(url);
    return axios.get(url);
  }

  studentLevelPasswordCheck(testId, password) {
    let enquiryId = localStorage.getItem("enquiryId");
    var dataString = {
      testId: testId,
      enquiryId: enquiryId,
      password: password,
    };
    // Node backend (tenant-flag opt-in).
    if (this.getOnlineTestBackend() === "node") {
      return myAxios.post(
        Config.nodeSiteUrl + "v1/online-tests/password/check/student-level",
        dataString
      );
    }
    return axios.post(
      Config.desktopApi + "common/test/password/check/studentLevel",
      dataString
    );
  }

  examTimetable(id) {
    return myAxios.get(Config.nodeSiteUrl + "v1/timetable/exam/" + id);
  }

  examList() {
    return myAxios.post(
      Config.nodeSiteUrl + "v1/timetable/exam-list"
    );
  }
  dossierSection() {
    let enquiryId = UserService.getEnquiryId();
    return myAxios.get(
      Config.siteUrl + "api/common/gradecard/enquiry/" + enquiryId
    );
  }

  mentorTranscripts() {
    let enquiryId = UserService.getEnquiryId();
    return myAxios.get(
      Config.siteUrl + "api/common/mentees/studentdetails/" + enquiryId
    );
  }
  discipline() {
    let enquiryId = UserService.getEnquiryId();
    return myAxios.get(
      Config.nodeSiteUrl + "v1/discipline/student/" + enquiryId
    );
  }

  convocationDetails() {
    let enquiryId = UserService.getEnquiryId();
    let url =
      Config.siteUrl +
      "api/common/convocation/student/details?enquiryId=" +
      enquiryId;
    return myAxios.get(url);
  }

  submitConvocationDetails(data) {
    let url = Config.siteUrl + "api/common/convocation/studentAttestation";
    return myAxios.post(url, data);
  }

  convocationStudentRegistration(data) {
    let url = Config.siteUrl + "api/common/convocation/studentRegistration";
    return myAxios.post(url, data);
  }

  async questionWiseAnalysis(body) {
    // On node-backend tenants the question-wise analysis comes from the nodefork
    // test-result endpoint, in the same { result, data: { sections, … } } shape
    // the legacy testAnalysis Lambda returned. Other tenants keep the Lambda.
    if (Config.onlineTestFns === "node") {
      const res = await this.fetchDirectTestResult(body.testId, body.enquiryId);
      const b = (res && res.data) || {};
      return { result: b.result, data: b.data };
    }
    // let enquiryId = UserService.getEnquiryId();
    // let url = Config.baseUrl + 'questionwiseanalysis/' + testid + '/' + enquiryId;
    const url = `${Config.lambda.testAnalysis}/`;
    console.log(url);
    return fetch(url, {
      method: "post",
      body: JSON.stringify(body),
    }).then((res) => res.json());
  }

  viewSolution(testId, currentIndex, attemptId) {
    const enquiryId = UserService.getEnquiryId();
    const url =
      `${Config.nodeSiteUrl}v1/test-result/${testId}/student/${enquiryId}/solution` +
      `?currentIndex=${currentIndex || 0}&attemptId=${attemptId || 0}`;
    return myAxios.get(url);
  }

  getTopRankers(testId) {
    const url = `${Config.nodeSiteUrl}v1/test-result/${testId}/toprankers`;
    return myAxios.get(url);
  }

  getTestStructure(testId) {
    const url = `${Config.nodeSiteUrl}v1/test-result/${testId}/structure`;
    return myAxios.get(url);
  }

  getStudentEnrollements() {
    const enquiryId = UserService.getEnquiryId();
    const url = `${Config.siteUrl}api/common/student/information?enquiryId=${enquiryId}`;
    return myAxios.get(url);
  }

  verifyMobile(mobile, queryString) {
    // const url = `${Config.siteUrl}api/mobile/user/otp/channel/mobile/identifier/${mobile}`;
    const url = `${Config.lambda.msg91Otp}/?mobileNumber=91${mobile}&action=sendOTP&tenant=${Config.siteTitle}${queryString}`;

    console.log(url);
    // http://triangles.edusquares.com/api/mobile/user/otp/channel/mobile/identifier/8168224633
    return axios.get(url);
  }

  loginWithOtp(mobile, otp, queryString) {
    // const url = `${Config.siteUrl}api/mobile/user/otp/verify`;
    // http://triangles.edusquares.com/api/mobile/user/otp/verify
    // return axios.post(url,payload);
    const url = `${Config.lambda.msg91Otp}/?mobileNumber=91${mobile}&action=verifyOTP&otp=${otp}&tenant=${Config.siteTitle}${queryString}`;
    console.log(url);
    return axios.get(url);
  }

  fetchTestDetails() {
    const url = `${Config.siteUrl}${Config.siteTitle}/rest/mobile/user/admission/testdetails`;
    // https://unsat.edusquares.com/unsat/rest/mobile/user/admission/testdetails
    return myAxios.get(url);
  }

  fetchGoogleAnalytics() {
    const url = `https://tuningfork-live.s3.ap-southeast-1.amazonaws.com/${Config.siteTitle}/files/app-properties.json`;
    console.log(url);
    return axios.get(url);
  }

  resendTextOtp = (mobile) => {
    const url = `${Config.lambda.msg91Otp}/?mobileNumber=91${mobile}&action=retryText&tenant=${Config.siteTitle}`;
    console.log(url);
    return axios.get(url);
  };

  resendCallOtp = (mobile) => {
    const url = `${Config.lambda.msg91Otp}/?mobileNumber=91${mobile}&action=retryVoice&tenant=${Config.siteTitle}`;
    console.log(url);
    return axios.get(url);
  };

  // Route a ported online-test Function-URL call to nodefork when the tenant's
  // deploy selects the node backend (Config.onlineTestFns === "node"), else to
  // the AWS Lambda URL. The node endpoints need an explicit JSON content-type
  // (plain fetch defaults to text/plain, which express.json() won't parse); the
  // Lambda URLs accept the raw body as before.
  onlineTestFnFetch = (fnName, nodePath, body) => {
    // Both backends are (or are becoming) JWT-authenticated: they identify the
    // student from the token rather than the body. Send the Bearer token to
    // either. The Lambda is accept-both during rollout, so this is harmless
    // until it's flipped to require auth.
    //
    // CORS NOTE: the Authorization header makes this a preflighted request, so
    // the Lambda Function URLs MUST allow it — testResponses/testResume CORS
    // was updated (20 Jul 2026) to AllowHeaders: [authorization, content-type],
    // AllowMethods: [POST]. If a new test Function URL is added here, configure
    // its CORS the same way or students get "Failed to fetch" (shown as the
    // "server is taking too long" modal).
    const authHeader = { Authorization: `Bearer ${UserService.getToken()}` };
    if (Config.onlineTestFns === "node") {
      return fetch(`${Config.nodeSiteUrl}${nodePath}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body,
      });
    }
    return fetch(`${Config.lambda[fnName]}/`, { method: "POST", headers: authHeader, body });
  };

  testResumes = (body) => {
    return this.onlineTestFnFetch("testResume", "v1/online-tests/fn/resume", body);
  };

  testResponses = (body) => {
    return this.onlineTestFnFetch("testResponses", "v1/online-tests/fn/responses", body);
  };

  // Category-level player config (SEB/lockdown, proctoring, …) from nodefork.
  // TestInstructions merges this over the paper's baked-in testConfig when the
  // paper is served by the legacy Lambda, which cannot read the live category
  // settings in tenant MySQL. The node serve path overlays it server-side.
  getTestPlayerCategoryConfig = (testId) => {
    return myAxios.get(`${Config.nodeSiteUrl}v1/online-tests/${testId}/player-config`);
  };

  testResultsUrls = (mobileNumber) => {
    const url = `${Config.siteUrl}api/mobile/user/admission/testresults?mobileNumber=${mobileNumber}`;
    console.log(url);
    return axios.get(url);
  };

  fetchGetUrl = (url) => {
    console.log(url);
    return axios.get(url);
  };

  updateProctoredProfile = (body) => {
    const url = `${Config.lambda.proctorProfile}/`;
    console.log(url);
    return fetch(url, {
      method: "POST",
      body,
    });
  };

  uploadDocument = (body) => {
    const enquiryId = UserService.getEnquiryId();
    const url = `${Config.siteUrl}api/mobile/ilearn/enquiry/${enquiryId}/document/upload`;
    console.log(url);
    return myAxios.post(url, body);
  };

  trackPage(data) {
    let url = `${Config.siteUrl}api/common/log/page`;
    console.log(url);
    return myAxios.post(url, data);
  }

  uploadDoucment = (body, testId, questionId) => {
    const userId = UserService.getUserId();
    // Node backend (tenant-flag opt-in): derives userId from the JWT, so the
    // S3 path/response match the Java handler.
    if (this.getOnlineTestBackend() === "node") {
      return myAxios.post(
        `${Config.nodeSiteUrl}v1/online-tests/${testId}/question/${questionId}/upload`,
        body
      );
    }
    const url = `${Config.siteUrl}api/common/student/${userId}/test/${testId}/question/${questionId}/upload`;
    console.log(url);
    return myAxios.post(url, body);
  };

  getTestSubmission = (testId) => {
    const enquiryId = UserService.getEnquiryId();
    const url = `${Config.siteUrl}rest/mobile/viewsolution/test/${testId}/enquiry/${enquiryId}/enrollment/0/currentIndex/1/attempt/0`;
    return axios.get(url);
  };

  fetchMode3ExternalResult = (id) => {
    const enquiryId = UserService.getEnquiryId();
    const url = `${Config.nodeSiteUrl}v1/test-result/${id}/student/${enquiryId}/external-result`;
    return myAxios.get(url);
  };

  checkForAttendance = (body) => {
    const url = `${Config.nodeSiteUrl}v1/student-tests/attendance-check`;
    console.log(url);
    return myAxios.post(url, body);
  };

  // DB-built question paper + per-question analysis from nodefork. This is the
  // authoritative source for online-test solution rendering: the endpoint is
  // JWT-protected AND honors "Make Solutions Public" (withholds the worked
  // solution text unless test_master.TestJsonPublic = 1). It replaces the
  // legacy `testSolutions` Lambda, which read the S3 solution JSON with
  // privileged IAM creds — bypassing the object ACL and returning solutions
  // with no auth and no public/private check. Returns
  // { testPaper, questionWiseAnalysis }.
  fetchQuestionPaperFromNode = (testId) => {
    const enquiryId = UserService.getEnquiryId();
    return myAxios
      .get(
        `${Config.nodeSiteUrl}v1/test-result/${testId}/student/${enquiryId}/question-paper`,
      )
      .then((res) => ({
        ...(res.data?.data || {}),
        status: res.data?.status,
        message: res.data?.message,
      }));
  };

  fetchDashboardUrl = (id) => {
    const url = `${Config.siteUrl}api/studyresources/getDashboardUrl/${id}`;
    console.log(url);
    return myAxios.get(url);
  };

  submitJeeAplicationDetails = (body) => {
    const url = `${Config.lambda.coins}`;
    console.log(url);
    return fetch(url, {
      method: "post",
      body: body,
    });
  };

  checkJeeAplicationDetails = (body) => {
    const url = `${Config.lambda.coins}?checkSave=true`;
    console.log(url);
    console.log(body);
    return fetch(url, {
      method: "post",
      body: JSON.stringify(body),
    });
  };

  fetchCurrentTime = () => {
    if (Config.onlineTestFns === "node") {
      return fetch(`${Config.nodeSiteUrl}v1/online-tests/fn/clock`, { method: "POST" }).then((res) => res.json());
    }
    return fetch(`${Config.lambda.clock}/`).then((res) => res.json());
  };

  saveUserData = (body) => {
    const url = `${Config.siteUrl}rest/mobile/enrollment/details/save`;
    console.log(url);
    return axios.post(url, body);
  };

  getUserFormId = (studentCode) => {
    const url = `${Config.siteUrl}rest/mobile/user/registration/form?mobileNumber=${studentCode}`;
    console.log(url);
    return myAxios.get(url);
  };

  fetchAdmitCardDetails = () => {
    const enquiryId = UserService.getEnquiryId();
    const url = `${Config.siteUrl}rest/mobile/user/enrollment/details?enquiryId=${enquiryId}`;
    console.log(url);
    return myAxios.get(url);
  };

  checkCoinsData = (body) => {
    const url = `${Config.lambda.coins}/?checkSubscription=true`;
    return fetch(url, {
      method: "post",
      body: JSON.stringify(body),
    }).then((res) => res.json());
  };

  deductCoins = (body) => {
    const url = `${Config.lambda.coins}/?deductCoins=true`;
    return fetch(url, {
      method: "post",
      body: JSON.stringify(body),
    }).then((res) => res.json());
  };

  fetchTestAnalysis = (categoryId) => {
    const enquiryId = UserService.getEnquiryId();
    const url = `${Config.nodeSiteUrl}v1/student-tests/performance/category/${Number(categoryId) || 0}/enquiry/${enquiryId}`;
    return myAxios.get(url);
  };

  fetchCentresData = (testId) => {
    const url = `${Config.nodeSiteUrl}v1/test-centre/centres?testId=${testId}`;
    console.log(url);
    return myAxios.get(url);
  };

  saveCentreDetails = (body) => {
    const url = `${Config.nodeSiteUrl}v1/test-centre/save`;
    console.log(url);
    return myAxios.post(url, body);
  };

  fetchZoomVideos = (enrollmentId = 0) => {
    const enquiryId = UserService.getEnquiryId();
    const url = `${Config.nodeSiteUrl}v1/timetable/activity-slot-resources/enquiry/${enquiryId}?enrollmentId=${enrollmentId || 0}`;
    console.log(url);
    return myAxios.get(url);
  };

  fetchUrl = (url) => {
    console.log(url, "here");
    return axios.get(url);
  };

  fetchFormData = (formId) => {
    const url = `${Config.siteUrl}api/common/form/${formId}/schemaJson`;
    console.log(url);
    return myAxios.get(url);
  };

  submitApplicationForm = (data) => {
    const url = `${Config.siteUrl}api/landing/jsonform/enquiry/create`;
    console.log(url);
    return axios.post(url, data);
  };

  fetchRecomendationEngine = () => {
    const userId = UserService.getUserId();
    const url = `${Config.nodeSiteUrl}v1/student-tests/recommendation/engine/user/${userId}`;
    return myAxios.get(url);
  };

  sendEmail = (body) => {
    const url = `${Config.siteUrl}rest/mobile/send/email`;
    console.log(url);
    return axios.post(url, body);
  };

  sendRegistrationMessage = (mobileNumber, name, date, time) => {
    const url = `${Config.lambda.msg91Otp}/?action=sendMessage&mobileNumber=91${mobileNumber}&stundentName=${name}&date=${date}${
      time ? `&time=${time}` : ""
    }`;
    console.log(url);
    return axios.get(url);
  };

  getStudentTicketTypes = () => {
    const url = `${Config.siteUrl}rest/students/ticket/student/types`;
    console.log(url);
    return myAxios.get(url);
  };

  sendStudentTicketDetails = (body) => {
    const url = `${Config.siteUrl}rest/students/ticket/details/save`;
    console.log(url);
    return myAxios.post(url, body);
  };

  fetchProgramDetails = (enrollmentId) => {
    // enquiryId url
    // const enquiryId = localStorage.getItem('enquiryId');
    // const url = `${Config.siteUrl}rest/students/student/${enquiryId}/program/details`;

    const url = `${Config.siteUrl}rest/students/student/enrollment/${enrollmentId}/program/details`;
    return myAxios.get(url);
  };

  fetchAllTest = () => {
    const enquiryId = localStorage.getItem("enquiryId");
    const url = `${Config.nodeSiteUrl}v1/student/${enquiryId}/all-tests`;
    return myAxios.get(url);
  };

  verifyMicrosoftToken = (token) => {
    const url = `${Config.lambda.verifyMicrosoftToken}/?tenant=${Config.siteTitle}`;
    return fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }).then((res) => res.json());
  };

  getMessageDetails = (userId) => {
    const url = `${Config.siteUrl}api/common/student/view/message/${userId}`;
    console.log(url);
    return axios.get(url);
  };

  getVdoCipherOtp = (videoId) => {
    const url = `${Config.lambda.vdocipher}/?videoId=${videoId}`;
    console.log(url);

    return myAxios.get(url);
  };

  fetchResourceTimeSpent = (body) => {
    const url = `${Config.siteUrl}api/common/generate/genericreport/report/8`;
    console.log(url);
    return myAxios.post(url, body);
  };

  unsatBatchUpdate = (body) => {
    const url = `${Config.siteUrl}rest/common/enrollment/batch/update`;
    console.log(url);
    return myAxios.post(url, body);
  };

  getS3SignedKey = (body) => {
    const url = `${Config.lambda.s3SignedKey}/?mode=getS3SignedKey`;
    console.log(url);
    return myAxios.post(url, body);
  };

  getLearningTimeSpent = (userName, credentials, baseUrl) => {
    async function fetchAllStatements(url, results = []) {
      console.log(url);
      const response = await axios.get(url, {
        headers: {
          Authorization: `Basic ${btoa(
            `${credentials.key}:${credentials.secret}`
          )}`,
          "x-experience-api-version": "1.0.1",
        },
      });
      results.push(...response.data.statements);

      if (response.data.more) {
        const nextUrl = `${baseUrl}${response.data.more}`;
        return fetchAllStatements(nextUrl, results);
      }

      return results;
    }

    return fetchAllStatements(
      `${baseUrl}/data/xAPI/statements?agent={"mbox": "https://${Config.siteTitle}.edusquares.com|${userName}"}&verb=http://edusquares.com/xapi/verbs/played&limit=1000`
    );
  };

  getAllCountries = () => {
    const url = `${Config.siteUrl}rest/common/countries`;

    return myAxios.get(url);
  };

  updateStudentAddress = (body) => {
    const url = `${Config.siteUrl}rest/students/address/update`;

    return myAxios.post(url, body);
  };

  acceptCashPayment = (body) => {
    const url = `${Config.siteUrl}api/students/pay/using/invoice`;
    console.log(url);
    return myAxios.post(url, body);
  };

  acceptCashPaymentWithToken = (body, token) => {
    const url = `${Config.siteUrl}api/students/pay/using/invoice`;
    console.log(url);
    return axios.post(url, body, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  };

  // Served by nodefork — same { status, message, data } shape as the old
  // Java rest/mobile/tags endpoint.
  fetchBrowseByDomainTags = () => {
    const url = `${Config.nodeSiteUrl}v1/tags/by-applies-to?appliesTo=product`;
    return myAxios.get(url);
  };

  // getProductViews was removed — the nodefork catalog (fetchProduct) now
  // includes a per-product `views` field, so no separate Java call is needed.

  // Daily Goal Progress Tracker — now served by nodefork (no Java dependency).
  // GET  returns { timeSpent (seconds today), dailyTargetInMinutes }
  // POST body { userId, dailyTargetInMinutes }
  setDailyTargetTime = (body) => {
    const url = `${Config.nodeSiteUrl}v1/learning/daily-target`;
    return myAxios.post(url, body);
  };

  fetchDailyTargetTime = (userId) => {
    const url = `${Config.nodeSiteUrl}v1/learning/daily-target/${userId}`;
    return myAxios.get(url);
  };

  updateLearningModuleStatus = (body) => {
    const url = `${Config.nodeSiteUrl}v1/learning-modules/status/save`;
    return myAxios.post(url, body);
  };

  initiateLearningModuleStatusRecord = (body) => {
    const url = `${Config.nodeSiteUrl}v1/learning-modules/initialize`;
    return myAxios.post(url, body);
  };

  // Avatar upload/delete moved to nodefork — same { type, message, url }
  // response contract as the old Java rest/mobile/ilearn avatar endpoints.
  createOrUpdateAvatar = (enquiryId, body) => {
    const url = `${Config.nodeSiteUrl}v1/students/${enquiryId}/avatar`;
    return myAxios.post(url, body);
  };

  deleteAvatar = (enquiryId) => {
    const url = `${Config.nodeSiteUrl}v1/students/${enquiryId}/avatar`;
    return myAxios.delete(url);
  };
  createHelpTicket = (body) => {
    const url = `${Config.siteUrl}rest/mobile/ilearn/ticket/add`;
    return myAxios.post(url, body);
  };
  updateProfile = (body) => {
    const url = `${Config.siteUrl}rest/landing/enquiry/update`;
    return myAxios.post(url, body);
  };

  updateAndFetchRSVPStatus = (body) => {
    const url = `${Config.siteUrl}api/common/user/rsvp`;
    return myAxios.post(url, body);
  };

  fetchHelpServiceData = () => {
    const url = `https://tuningfork-static-file-server.s3.ap-southeast-1.amazonaws.com/assets/tenants/${Config.siteTitle}/customer-details.json`;
    console.log(url);
    return axios.get(url);
  };

  getUserStudyResourceBookmarks() {
    const url = `${Config.siteUrl}api/common/fetch/user/${UserService.getUserId()}/bookmarks`;
    return myAxios.get(url);
  }

  // Get bookmark status for a specific resource
  getBookmarkStatus(resourceId, userId) {
    const url =
      Config.nodeSiteUrl +
      "v1/study-resources/" +
      resourceId +
      "/user/" +
      userId +
      "/bookmark";
    return myAxios.get(url);
  }

  // Toggle bookmark for a specific resource
  toggleBookmark(resourceId, userId, bookmark) {
    const url =
      Config.nodeSiteUrl +
      "v1/study-resources/" +
      resourceId +
      "/bookmark";

    const payload = {
      userId: userId,
      bookmark: bookmark
    };

    return myAxios.post(url, payload);
  }

  // Legacy method - keeping for backward compatibility if needed elsewhere
  userStudyResourceBookmarks(body) {
    const url = `${Config.siteUrl}api/common/bookmark/user/studyresource`;
    return myAxios.post(url, body);
  }

  getAlumniRegistrationFormData = async () => {
    const url = `${Config.nodeSiteUrl}v1/alumni/registration-form`;

    return axios.get(url);
  };

  registerAlumni = (data) => {
    const url = `${Config.nodeSiteUrl}v1/alumni`;
    return axios.post(url, data);
  };

  fetchUserCertificates = (userId) => {
    const url = `${Config.siteUrl}api/common/fetch/user/${userId}/certificates`;
    return myAxios.get(url);
  };

  checkUniqueUsername = (username) => {
    const url = `${Config.nodeSiteUrl}v1/users/check-username?username=${username}`;
    console.log(url);
    return axios.get(url);
  };

  fetchAllUserNotes = () => {
    let url = `${
      Config.siteUrl
    }api/common/fetch/user/${UserService.getUserId()}/notes`;
    return myAxios.get(url);
  };

  getActivitySlotTemplates = (body) => {
    const url = `${Config.siteUrl}api/common/fetch/activityslot/templates`;
    return myAxios.post(url, body);
  };

  scheduleActivitySlotForUser = (body) => {
    const url = `${Config.siteUrl}api/common/schedule/user/activityslot`;
    return myAxios.post(url, body);
  };

  fetchUserScheduledActivitySlots = (body) => {
    const url = `${Config.siteUrl}api/common/fetch/user/activityslot/booking`;
    return myAxios.post(url, body);
  };

  getStudentSchedule = (enrollmentId, params) => {
    const url = `${Config.nodeSiteUrl}v1/schedules/student/${enrollmentId}`;
    console.log(url);
    return myAxios.get(url, { params });
  };

  verifyValidUser = () => {
    const userId = UserService.getUserId();
    const baseUrl = Config.authBackend === 'node' ? `${Config.nodeSiteUrl}v1/` : Config.siteUrl;
    const url = `${baseUrl}rest/mobile/verifyuser/${userId}/status`;
    return myAxios.get(url);
  };

  fetchProgramIntakes = () => {
    const url = `${Config.nodeSiteUrl}v1/program-details`;
    console.log(url);
    return axios.get(url);
  };

  fetchProgramModules = (programId, intakeId) => {
    const url = `${Config.nodeSiteUrl}v1/program-details/programs/${programId}/intakes/${intakeId}`;
    console.log(url);
    return axios.get(url);
  };

  fetchDefaultCurrency = () => {
    const url = `${Config.nodeSiteUrl}v1/currencies/default`;
    console.log(url);
    return axios.get(url);
  };

  fetchDeliveryFormats = () => {
    const url = `${Config.nodeSiteUrl}v1/program-details/delivery-formats`;
    console.log(url);
    return axios.get(url);
  };

  saveCartData = (body)=>{
    const url = `${Config.nodeSiteUrl}v1/cart`;
    console.log(url);
    return axios.post(url, body);
  }

  trackCertifyingLetterDownload = (userId, verb, enrollmentId) => {
    const url = `${Config.baseUrl}track/certifying/letter/downloads`;
    const data = {
      userId: userId,
      verb: verb,
      enrollmentId: enrollmentId
    };
    console.log(url);
    return myAxios.post(url, data);
  }

  
  registerModules = (body)=>{
    const url = `${Config.siteUrl}api/common/request/course/approval/advisor`
    return myAxios.post(url, body);
  }
  deRegisterModules = (body)=>{
    const url = `${Config.siteUrl}api/common/request/course/deregister`
    return myAxios.post(url, body);
  }
  fetchLearningAnalytics = (userId) => {
    const url = `${Config.siteUrl}api/studyresources/user/${userId}/learninganalytics`;
    console.log(url);
    return myAxios.get(url);
  }

  fetchTickets = (body)=>{
    const url = `${Config.siteUrl}api/students/ticket/details/search`;
    return myAxios.post(url, body);
  }

  fetchGoldenStudyResources(postData) {
    const url = `${Config.nodeSiteUrl}v1/study-resources/student/golden`;
    const payload = {
      ...postData,
      fromLearningModules: postData.fromLearningModules !== undefined ? postData.fromLearningModules : false
    };
    console.log(url);
    return myAxios.post(url, payload);
  }

  fetchRecentGoldenOnlineTests(postData) {
    const url = `${Config.siteUrl}api/golden/recent/onlineTests`;
    const payload = {
      ...postData,
      fromLearningModules: postData.fromLearningModules !== undefined ? postData.fromLearningModules : false
    };
    console.log(url);
    return myAxios.post(url, payload);
  }

    generateQueryResponse = (body) => {
    const url = `${Config.nodeSiteUrl}v1/gen-ai/prompt`;
    return myAxios.post(url, body);
  };

  // PlayGround APIs
  getPlaygroundTags() {
    return myAxios.get(Config.nodeSiteUrl + "v1/playground/tags");
  }

  getPlaygroundTagChildren(tagId) {
    return myAxios.get(Config.nodeSiteUrl + "v1/playground/tags/" + tagId);
  }

  getPlaygroundQuestions(filters) {
    return myAxios.post(Config.nodeSiteUrl + "v1/playground/questions", filters);
  }

  savePlaygroundAttempt(data) {
    return myAxios.post(Config.nodeSiteUrl + "v1/playground/attempt/save", data);
  }

  getPlaygroundSolution(questionId) {
    return myAxios.get(Config.nodeSiteUrl + "v1/playground/questions/" + questionId + "/solution");
  }

  postPlaygroundDoubt(data) {
    return myAxios.post(Config.nodeSiteUrl + "v1/playground/questions/doubt/post", data);
  }

  getPlaygroundFullSyllabusTests() {
    return myAxios.get(Config.nodeSiteUrl + "v1/playground/full-syllabus-tests");
  }

  getPlaygroundCompetencyTags(subjectTagId) {
    return myAxios.get(Config.nodeSiteUrl + "v1/playground/competency-tags?subjectTagId=" + subjectTagId);
  }

  getPlaygroundAnalyticsOverview() {
    return myAxios.get(Config.nodeSiteUrl + "v1/playground/analytics/overview");
  }

  getPlaygroundAnalyticsPerformance() {
    return myAxios.get(Config.nodeSiteUrl + "v1/playground/analytics/performance");
  }

  getPlaygroundAnalyticsWeakAreas() {
    return myAxios.get(Config.nodeSiteUrl + "v1/playground/analytics/weak-areas");
  }
  // Proctored Tests
  getProctoredTests() {
    const enquiryId = localStorage.getItem("enquiryId");
    const url = `${Config.nodeSiteUrl}v1/proctored-tests/student/${enquiryId}`;
    return myAxios.get(url);
  }

  getAvailableProctoredTestSlots(testId) {
    const enquiryId = localStorage.getItem("enquiryId");
    const url = `${Config.nodeSiteUrl}v1/proctored-tests/${testId}/slots?enquiryId=${enquiryId}`;
    return myAxios.get(url);
  }

  bookProctoredTestSlot(testId, slotId) {
    const enquiryId = localStorage.getItem("enquiryId");
    const url = `${Config.nodeSiteUrl}v1/proctored-tests/book-slot`;
    return myAxios.post(url, { enquiryId: Number(enquiryId), testId, slotId });
  }

  getProctoredTestTicket(bookingId) {
    const url = `${Config.nodeSiteUrl}v1/proctored-tests/ticket/${bookingId}`;
    return myAxios.get(url);
  }

  // Screen proctoring endpoints
  getRecordingUploadUrl(testId, recordingType, chunkIndex) {
    const url = `${Config.nodeSiteUrl}v1/proctored-tests/upload-url`;
    return myAxios.post(url, { testId, recordingType, chunkIndex });
  }

  logBrowserViolation(testId, violationType, metadata) {
    const url = `${Config.nodeSiteUrl}v1/proctored-tests/log-violation`;
    return myAxios.post(url, { testId, violationType, metadata });
  }

  registerRecordingChunk(testId, recordingType, s3Key, s3Bucket, chunkIndex, fileSizeBytes) {
    const url = `${Config.nodeSiteUrl}v1/proctored-tests/register-recording`;
    return myAxios.post(url, { testId, recordingType, s3Key, s3Bucket, chunkIndex, fileSizeBytes });
  }

  // Publish the category's webcam analysis rules for this test so the
  // proctoring lambda picks them up (tenant must match the proctoring iframe's
  // tenant param — both use Config.siteTitle).
  publishProctoringRules(testId) {
    const url = `${Config.nodeSiteUrl}v1/proctored-tests/publish-analysis-rules`;
    return myAxios.post(url, { testId, tenant: Config.siteTitle });
  }

  verifyFace(payload) {
    const url = `${Config.lambda.testAnalysis}/?mode=verifyFace`;
    return fetch(url, {
      method: "POST",
      body: JSON.stringify(payload),
    }).then((res) => res.json());
  }

  // ─── Marketplace ────────────────────────────────────────────────────
  fetchMarketplaceProviders = () => {
    return myAxios.get(`${Config.nodeSiteUrl}v1/marketplace/providers`);
  };

  fetchMarketplaceContent = (params = {}) => {
    return myAxios.get(`${Config.nodeSiteUrl}v1/marketplace/content`, { params });
  };

  subscribeToContent = (body) => {
    const enquiryId = Number(localStorage.getItem("enquiryId")) || undefined;
    return myAxios.post(`${Config.nodeSiteUrl}v1/marketplace/subscribe`, { ...body, enquiryId });
  };

  fetchMyEntitlements = () => {
    const enquiryId = Number(localStorage.getItem("enquiryId")) || undefined;
    return myAxios.get(`${Config.nodeSiteUrl}v1/marketplace/my-entitlements`, { params: { enquiryId } });
  };
}

export default new ApiService();
